
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toast } from 'sonner';

export const useContactRecovery = () => {
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryCompleted, setRecoveryCompleted] = useState(false);
  const [pendingContacts, setPendingContacts] = useState<any[]>([]);
  const { trackEvent } = useAnalytics();
  
  // Check for pending contacts on component mount
  useEffect(() => {
    checkForPendingContacts();
  }, []);
  
  const checkForPendingContacts = () => {
    try {
      const storedContacts = localStorage.getItem('pendingContacts');
      if (storedContacts) {
        const contacts = JSON.parse(storedContacts);
        if (Array.isArray(contacts) && contacts.length > 0) {
          console.log(`Found ${contacts.length} pending contacts to recover`);
          
          // Track the detection of pending contacts
          trackEvent('pending_contacts_found', {
            count: contacts.length
          });
          
          setPendingContacts(contacts);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking for pending contacts:', error);
      
      // Track the error
      trackEvent('error', { 
        context: 'contact_recovery_check',
        message: error instanceof Error ? error.message : String(error)
      });
      
      return false;
    }
  };
  
  const recoverPendingContacts = async () => {
    if (pendingContacts.length === 0) {
      // Try to check again in case contacts were added after initial load
      const hasPending = checkForPendingContacts();
      if (!hasPending) return false;
    }
    
    try {
      setIsRecovering(true);
      
      // Track start of recovery process
      trackEvent('contact_recovery_started', {
        count: pendingContacts.length
      });
      
      // Call the edge function to process pending contacts
      const { data, error } = await supabase.functions.invoke('process-pending-contacts', {
        body: { pendingContacts }
      });
      
      if (error) {
        console.error('Error recovering pending contacts:', error);
        
        // Track the error
        trackEvent('error', { 
          context: 'contact_recovery_process',
          message: error.message
        });
        
        setIsRecovering(false);
        
        toast.error('Virhe yhteystietojen palautuksessa', {
          description: 'Tapahtui virhe kun yritimme palauttaa yhteystietojasi. Yritä myöhemmin uudelleen.'
        });
        
        return false;
      }
      
      if (data.processed > 0) {
        console.log(`Successfully recovered ${data.processed} contacts`);
        
        // Track successful recovery
        trackEvent('contact_recovery_success', {
          processed: data.processed,
          failed: data.failed || 0
        });
        
        // Remove the successfully processed contacts
        const successfulIds = data.results
          .filter((r: any) => r.status === 'fulfilled' && r.value.success)
          .map((r: any) => r.value.id);
          
        if (successfulIds.length > 0) {
          const remainingContacts = pendingContacts.filter(
            (contact) => !successfulIds.includes(contact.id)
          );
          
          // Update localStorage with only the failed contacts
          localStorage.setItem('pendingContacts', JSON.stringify(remainingContacts));
          setPendingContacts(remainingContacts);
          
          toast.success('Yhteystietojen palautus onnistui', {
            description: `${data.processed} yhteystietoa palautettu onnistuneesti.`
          });
        }
        
        setRecoveryCompleted(true);
        setIsRecovering(false);
        return true;
      } else {
        console.log('No contacts could be recovered');
        
        // Track zero contacts recovered
        trackEvent('contact_recovery_zero', {
          attempted: pendingContacts.length
        });
        
        setIsRecovering(false);
        
        toast.warning('Ei palautettavia yhteystietoja', {
          description: 'Yhteystietoja ei voitu palauttaa. Tarkista verkkoyhteys ja yritä uudelleen.'
        });
        
        return false;
      }
    } catch (error) {
      console.error('Error in contact recovery process:', error);
      
      // Track the error
      trackEvent('error', { 
        context: 'contact_recovery_process',
        error: error instanceof Error ? error.message : String(error)
      });
      
      setIsRecovering(false);
      
      toast.error('Virhe yhteystietojen palautuksessa', {
        description: 'Tapahtui odottamaton virhe. Yritä myöhemmin uudelleen.'
      });
      
      return false;
    }
  };
  
  return {
    hasPendingContacts: pendingContacts.length > 0,
    pendingContacts,
    isRecovering,
    recoveryCompleted,
    recoverPendingContacts
  };
};
